import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, idr, fmtNum } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { bolehPakaiAI } from "../lib/izinAI";
import { UserCheck, UserX, ShoppingBag, TrendingUp, AlertTriangle, Clock, ArrowUpRight, ArrowDownRight, Minus, Ticket, Printer } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { hariIniISO, hariIniDate, isoDariDate, formatTanggal } from "@/lib/tanggal";
import LoadingState from "@/components/ui/loading-state";
import { useDataQuery } from "@/hooks/useDataQuery";
import DashboardWorkspace from "@/components/hris/DashboardWorkspace";
import MobileAIInsight from "@/components/hris/MobileAIInsight";

// Diekspor supaya Layout.jsx bisa prefetch query yang sama persis saat kursor
// hover menu "Dashboard" cukup lama — satu-satunya sumber kebenaran untuk key ini.
export const DASHBOARD_QK = ["dashboard"];
export const DASHBOARD_FINANCE_QK = ["dashboard-finance"];
export const DASHBOARD_AKTIVITAS_QK = ["dashboard-aktivitas"];

const Kpi = ({ icon: Icon, label, value, tint = "emerald", delay = 0, tid, sub }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
    data-testid={tid}
    className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm"
  >
    <div className="flex items-start justify-between mb-3 md:mb-6">
      <div className={`w-9 h-9 md:w-10 md:h-10 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600`}>
        <Icon className="w-4 h-4 md:w-5 md:h-5" />
      </div>
    </div>
    <p className="text-[10px] md:text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
    <p className="font-display text-xl md:text-2xl font-bold text-slate-900 truncate">{value}</p>
    {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
  </motion.div>
);

function AIInsightCard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState("yesterday"); // yesterday | last_week | last_month | custom
  const [customStart, setCustomStart] = useState(() => {
    const d = hariIniDate(); d.setDate(d.getDate() - 7);
    return isoDariDate(d);
  });
  const [customEnd, setCustomEnd] = useState(() => {
    const d = hariIniDate(); d.setDate(d.getDate() - 1);
    return isoDariDate(d);
  });

  const fetchInsight = useCallback(async (refresh = false, p = period, cs = customStart, ce = customEnd) => {
    (refresh ? setRefreshing : setLoading)(true);
    try {
      let url = `/ai/dashboard-insight?period=${p}${refresh ? "&refresh=true" : ""}`;
      if (p === "custom") url += `&start=${cs}&end=${ce}`;
      const result = await queryClient.fetchQuery({
        queryKey: ["dashboard-ai-insight", user?.id, p, p === "custom" ? cs : null, p === "custom" ? ce : null],
        queryFn: () => api.get(url).then(r => r.data),
        staleTime: refresh ? 0 : 60_000,
      });
      setInsight(result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat AI Insight");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period, customStart, customEnd, queryClient, user?.id]);

  useEffect(() => {
    // auto-fetch untuk preset periods; custom butuh user klik "Terapkan"
    if (period !== "custom") fetchInsight(false, period);
  }, [period, fetchInsight]);

  const genStr = insight?.generated_at ? formatTanggal(insight.generated_at, { denganJam: true }) : "-";

  return <MobileAIInsight insight={insight} loading={loading} refreshing={refreshing}
    period={period} setPeriod={setPeriod} customStart={customStart} setCustomStart={setCustomStart}
    customEnd={customEnd} setCustomEnd={setCustomEnd} today={hariIniISO()} generatedLabel={genStr}
    onRefresh={() => fetchInsight(true, period, customStart, customEnd)}
    onApply={() => fetchInsight(true, "custom", customStart, customEnd)} />;

}

function WowTrendPill({ pct, up, flat }) {
  const Icon = flat ? Minus : (up ? ArrowUpRight : ArrowDownRight);
  const cls = flat
    ? "bg-slate-100 text-slate-600 border-slate-200"
    : up
      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
      : "bg-rose-100 text-rose-700 border-rose-200";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-semibold ${cls}`}>
      <Icon className="w-3.5 h-3.5" />
      {flat ? "0%" : `${up ? "+" : ""}${pct}%`}
    </span>
  );
}

function WowMiniBars({ tw_daily = [], lw_daily = [] }) {
  const twMax = Math.max(...tw_daily.map(d => d.omzet || 0), 1);
  const lwMax = Math.max(...lw_daily.map(d => d.omzet || 0), 1);
  const maxVal = Math.max(twMax, lwMax);
  const labels = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
  return (
    <div className="mt-3 flex items-end justify-between gap-1.5 h-16">
      {labels.map((lbl, i) => {
        const twVal = tw_daily[i]?.omzet || 0;
        const lwVal = lw_daily[i]?.omzet || 0;
        const twH = (twVal / maxVal) * 56;
        const lwH = (lwVal / maxVal) * 56;
        const hasThisWeek = i < tw_daily.length;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex items-end justify-center gap-0.5 h-14">
              <div className="w-1/2 bg-slate-300/70 rounded-t-sm" style={{ height: `${lwH}px` }} title={`Minggu Lalu: ${lwVal.toLocaleString("id-ID")}`} />
              <div className={`w-1/2 rounded-t-sm ${hasThisWeek ? "bg-emerald-500" : "bg-slate-100"}`} style={{ height: `${hasThisWeek ? twH : 0}px` }} title={`Minggu Ini: ${twVal.toLocaleString("id-ID")}`} />
            </div>
            <span className="text-[9px] text-slate-500">{lbl}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ================= Attendance Week-over-Week Comparison Card ================= */
function AttWowRow({ label, twVal, lwVal, pct, upBad, icon: I, tint }) {
  const flat = pct === 0;
  const up = pct > 0;
  const isGood = flat ? null : (upBad ? !up : up);
  const cls = flat ? "text-slate-500" : isGood ? "text-emerald-700" : "text-rose-700";
  const Arrow = flat ? Minus : (up ? ArrowUpRight : ArrowDownRight);
  return (
    <div className="border border-slate-200 rounded-xl bg-white p-4" data-testid={`att-wow-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <I className={`w-3.5 h-3.5 text-${tint}-600`} />
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        </div>
        <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${cls}`}>
          <Arrow className="w-3 h-3" />
          {flat ? "0%" : `${up ? "+" : ""}${pct}%`}
        </span>
      </div>
      <p className="font-display text-2xl font-bold text-slate-900">{twVal}</p>
      <p className="text-xs text-slate-500 mt-1">
        Minggu lalu: <span className="font-mono text-slate-700">{lwVal}</span>
      </p>
    </div>
  );
}

function AttendanceWowCard({ att }) {
  const tw = att.this_week || {};
  const lw = att.last_week || {};
  const d = att.delta || {};

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
      data-testid="attendance-wow-card"
      className="mb-6 md:mb-8 bg-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-sm"
    >
      <div className="mb-4">
        <h3 className="font-display font-bold text-slate-900 text-base md:text-lg inline-flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-sky-600" />
          Absensi Minggu Ini vs Minggu Lalu
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Trend disiplin tim · perbandingan hari kerja yang sama · headcount aktif: <b>{Math.round((tw.expected || 0) / Math.max(1, (att.this_week?.n_hadir + tw.n_alpa) > 0 ? 1 : 1))}</b>
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <AttWowRow label="Hadir" twVal={tw.n_hadir || 0} lwVal={lw.n_hadir || 0}
          pct={d.hadir_pct || 0} upBad={false} icon={UserCheck} tint="emerald" />
        <AttWowRow label="Telat" twVal={tw.n_telat || 0} lwVal={lw.n_telat || 0}
          pct={d.telat_pct || 0} upBad={true} icon={Clock} tint="amber" />
        <AttWowRow label="Lembur" twVal={tw.n_lembur || 0} lwVal={lw.n_lembur || 0}
          pct={d.lembur_pct || 0} upBad={false} icon={TrendingUp} tint="sky" />
        <AttWowRow label="Alpa" twVal={tw.n_alpa || 0} lwVal={lw.n_alpa || 0}
          pct={d.alpa_pct || 0} upBad={true} icon={UserX} tint="rose" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
          <p className="text-slate-500 mb-1">Total menit telat</p>
          <p className="font-mono font-semibold text-slate-900">{tw.total_late_min || 0}m <span className="text-slate-400">vs {lw.total_late_min || 0}m minggu lalu</span></p>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
          <p className="text-slate-500 mb-1">Total menit lembur</p>
          <p className="font-mono font-semibold text-slate-900">{tw.total_ot_min || 0}m <span className="text-slate-400">vs {lw.total_ot_min || 0}m minggu lalu</span></p>
        </div>
      </div>
    </motion.section>
  );
}

/* ================= Week-over-Week Comparison Card ================= */
function WowCompareCard({ wow }) {
  const tw = wow.this_week || {};
  const lw = wow.last_week || {};
  const d = wow.delta || {};
  const omzetPct = d.omzet_pct || 0;
  const countPct = d.count_pct || 0;
  const acqPct = d.acq_pct || 0;
  const omzetUp = omzetPct > 0;
  const omzetFlat = omzetPct === 0;
  const countUp = countPct > 0;
  const countFlat = countPct === 0;
  const acqUp = acqPct > 0;
  const acqFlat = acqPct === 0;

  const fmtDate = (iso) => {
    if (!iso) return "";
    const dt = new Date(iso + "T00:00:00");
    return dt.toLocaleDateString("id-ID", { day: "numeric", month: "short" });
  };
  const twRange = `${fmtDate(tw.start)} – ${fmtDate(tw.end)}`;
  const lwSameEnd = (() => {
    if (!lw.start || !tw.end || !tw.start) return "";
    const twStartDt = new Date(tw.start + "T00:00:00");
    const twEndDt = new Date(tw.end + "T00:00:00");
    const daysDiff = Math.round((twEndDt - twStartDt) / 86400000);
    const lwStartDt = new Date(lw.start + "T00:00:00");
    const sameEnd = new Date(lwStartDt.getTime() + daysDiff * 86400000);
    return sameEnd.toLocaleDateString("id-ID", { day: "numeric", month: "short" });
  })();
  const lwRange = lw.start ? `${fmtDate(lw.start)} – ${lwSameEnd}` : "";

  const idrCompact = (v) => {
    v = Number(v || 0);
    if (v >= 1e9) return `Rp ${(v / 1e9).toFixed(2)}M`;
    if (v >= 1e6) return `Rp ${(v / 1e6).toFixed(1)}jt`;
    if (v >= 1e3) return `Rp ${(v / 1e3).toFixed(0)}rb`;
    return `Rp ${v.toLocaleString("id-ID")}`;
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
      data-testid="wow-compare-card"
      className="mb-6 md:mb-8 bg-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-sm"
    >
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-display font-bold text-slate-900 text-base md:text-lg inline-flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Minggu Ini vs Minggu Lalu
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Perbandingan hari yang sama · <span className="font-medium text-slate-700">{twRange}</span> vs <span className="text-slate-500">{lwRange}</span>
          </p>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm" /> Minggu Ini</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-slate-300 rounded-sm" /> Minggu Lalu</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Omzet compare */}
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-emerald-50/60 to-white p-4" data-testid="wow-omzet-block">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Omzet Penjualan</p>
            <WowTrendPill pct={omzetPct} up={omzetUp} flat={omzetFlat} />
          </div>
          <p className="font-display text-2xl font-bold text-slate-900" data-testid="wow-omzet-tw">{idrCompact(tw.omzet || 0)}</p>
          <p className="text-xs text-slate-500 mt-1">
            Minggu lalu (hari sama): <span className="font-mono text-slate-700">{idrCompact(lw.omzet_same || 0)}</span>
          </p>
          <p className="text-[11px] mt-1">
            Selisih:
            <span className={`ml-1 font-mono font-semibold ${omzetFlat ? "text-slate-600" : omzetUp ? "text-emerald-700" : "text-rose-700"}`}>
              {(d.omzet_abs || 0) >= 0 ? "+" : ""}{idrCompact(d.omzet_abs || 0)}
            </span>
          </p>
          <WowMiniBars tw_daily={tw.daily || []} lw_daily={lw.daily || []} />
        </div>

        {/* Transaksi compare */}
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-sky-50/60 to-white p-4" data-testid="wow-count-block">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Jumlah Transaksi</p>
            <WowTrendPill pct={countPct} up={countUp} flat={countFlat} />
          </div>
          <p className="font-display text-2xl font-bold text-slate-900" data-testid="wow-count-tw">{fmtNum(tw.count || 0)} tx</p>
          <p className="text-xs text-slate-500 mt-1">
            Minggu lalu (hari sama): <span className="font-mono text-slate-700">{fmtNum(lw.count_same || 0)} tx</span>
          </p>
          <p className="text-[11px] mt-1">
            Selisih:
            <span className={`ml-1 font-mono font-semibold ${countFlat ? "text-slate-600" : countUp ? "text-emerald-700" : "text-rose-700"}`}>
              {(d.count_abs || 0) >= 0 ? "+" : ""}{fmtNum(d.count_abs || 0)} tx
            </span>
          </p>
          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
            <span>Rata-rata / hari</span>
            <span className="font-mono text-slate-700">
              {fmtNum(Math.round((tw.count || 0) / Math.max((tw.daily || []).length, 1)))} vs {fmtNum(Math.round((lw.count_same || 0) / Math.max((tw.daily || []).length, 1)))}
            </span>
          </div>
        </div>

        {/* Akuisisi compare */}
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-amber-50/60 to-white p-4" data-testid="wow-acq-block">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Akuisisi Unit</p>
            <WowTrendPill pct={acqPct} up={acqUp} flat={acqFlat} />
          </div>
          <p className="font-display text-2xl font-bold text-slate-900" data-testid="wow-acq-tw">{fmtNum(tw.acq_count || 0)} unit</p>
          <p className="text-xs text-slate-500 mt-1">
            Minggu lalu (hari sama): <span className="font-mono text-slate-700">{fmtNum(lw.acq_count_same || 0)} unit</span>
          </p>
          <div className={`mt-2 rounded-lg p-2 text-xs ${acqFlat ? "bg-slate-50 text-slate-600" : acqUp ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
            {acqFlat
              ? "Akuisisi stabil dibanding minggu lalu."
              : acqUp
                ? `Naik ${acqPct}% dari minggu lalu — momentum baik.`
                : `Turun ${Math.abs(acqPct)}% dari minggu lalu — perlu dorong akuisisi.`}
          </div>
        </div>
      </div>

      {/* Overall verdict banner */}
      <div className={`mt-4 rounded-xl p-3 text-sm border flex items-center gap-2 ${
        omzetFlat ? "bg-slate-50 border-slate-200 text-slate-700" :
        omzetUp   ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
                    "bg-rose-50 border-rose-200 text-rose-800"
      }`}>
        {omzetFlat ? <Minus className="w-4 h-4" /> : omzetUp ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
        <span data-testid="wow-verdict">
          {omzetFlat
            ? "Omzet sama dengan minggu lalu di periode yang sama."
            : omzetUp
              ? `Omzet naik ${omzetPct}% (${idrCompact(d.omzet_abs || 0)}) dibanding minggu lalu di periode yang sama.`
              : `Omzet turun ${Math.abs(omzetPct)}% (${idrCompact(d.omzet_abs || 0)}) dibanding minggu lalu di periode yang sama.`}
        </span>
      </div>
    </motion.section>
  );
}

/** Format timestamp log aktivitas (WIB ISO string) ke label ringkas id-ID. */
function SeksiAntrian({ antrian, bolehBuka }) {
  const h = antrian.hari_ini || {};
  const b = antrian.bulan_ini || {};
  const perCabang = antrian.per_cabang || [];
  const sejak = antrian.penanda_cetak_sejak;
  // Tiket yang terbit sebelum penanda cetak ada terbaca "belum dicetak".
  // Tanpa catatan ini, angka dicetak yang rendah di awal terlihat seperti
  // tiket yang tidak dicetak, padahal cuma belum tercatat.
  const perluCatatanCetak = b.terbit > 0 && (!sejak || sejak > antrian.dari);
  return (
    <section className="mb-8 md:mb-10" data-testid="dash-antrian">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h2 className="font-display text-base md:text-lg font-bold text-slate-900">Antrian Pelanggan</h2>
        <div className="flex items-center gap-2">
          {bolehBuka && (
            <Link to="/antrian?tab=antrean" data-testid="dash-antrian-buka"
                  className="text-[11px] md:text-xs text-emerald-700 hover:underline">Buka Antrian</Link>
          )}
          <span className="text-[11px] md:text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium">Bulan Ini</span>
        </div>
      </div>
      {antrian.pesan_cabang && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2" data-testid="dash-antrian-pesan-cabang">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">{antrian.pesan_cabang}</p>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mb-4">
        <Kpi tid="kpi-antrian-dicetak-hari-ini" icon={Printer} label="Tiket Dicetak Hari Ini" value={fmtNum(h.dicetak || 0)}
             sub={`dari ${fmtNum(h.terbit || 0)} nomor terbit${h.cetak_ulang ? ` · ${fmtNum(h.cetak_ulang)} cetak ulang` : ""}`}
             tint="emerald" delay={0} />
        <Kpi tid="kpi-antrian-dicetak-bulan-ini" icon={Printer} label="Tiket Dicetak Bulan Ini" value={fmtNum(b.dicetak || 0)}
             sub={`dari ${fmtNum(b.terbit || 0)} nomor terbit${b.cetak_ulang ? ` · ${fmtNum(b.cetak_ulang)} cetak ulang` : ""}`}
             tint="sky" delay={0.05} />
        <Kpi tid="kpi-antrian-menunggu" icon={Ticket} label="Masih Menunggu Hari Ini" value={fmtNum(h.menunggu || 0)}
             sub={`${fmtNum(h.dipanggil || 0)} sedang dilayani`} tint="amber" delay={0.1} />
        <Kpi tid="kpi-antrian-membeli" icon={ShoppingBag} label="Membeli Bulan Ini" value={fmtNum(b.membeli || 0)}
             sub={`${fmtNum(b.tidak_membeli || 0)} tidak membeli · ${fmtNum(b.tidak_hadir || 0)} tidak hadir`}
             tint="slate" delay={0.15} />
      </div>
      {perluCatatanCetak && (
        <p className="text-[11px] text-slate-500 mb-3" data-testid="dash-antrian-catatan-cetak">
          {sejak
            ? `Catatan cetak baru tersimpan sejak ${formatTanggal(sejak)} — tiket yang terbit sebelumnya terhitung belum dicetak.`
            : "Belum ada tiket yang tercatat dicetak bulan ini. Catatan cetak mulai tersimpan begitu tombol Cetak Tiket ditekan."}
        </p>
      )}
      {perCabang.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-center text-xs text-slate-400" data-testid="dash-antrian-kosong">
          Belum ada nomor antrian terbit bulan ini
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-xs md:text-sm" data-testid="dash-antrian-per-cabang">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left font-medium px-3 py-2">Cabang</th>
                <th className="text-right font-medium px-3 py-2">Terbit</th>
                <th className="text-right font-medium px-3 py-2">Dicetak</th>
                <th className="text-right font-medium px-3 py-2">Cetak ulang</th>
                <th className="text-right font-medium px-3 py-2">Membeli</th>
              </tr>
            </thead>
            <tbody>
              {perCabang.map((c, i) => (
                <tr key={c.cabang} className="border-t border-slate-100" data-testid={`dash-antrian-cabang-${i}`}>
                  <td className="px-3 py-2 text-slate-900">{c.cabang}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(c.terbit)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(c.dicetak)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(c.cetak_ulang)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(c.membeli)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function Dashboard() {
  const { user, can, canWidget } = useAuth();
  const { data, error: dashError } = useDataQuery(
    DASHBOARD_QK,
    () => api.get("/dashboard").then(r => r.data),
  );
  useEffect(() => {
    if (dashError) toast.error(dashError?.response?.data?.detail || "Gagal memuat dashboard.");
  }, [dashError]);

  // Ringkasan keuangan (kas/bank, piutang, utang, aging) memakai endpoint Finance
  // yang sudah ada — bukan query baru. Ditembak HANYA kalau user berizin
  // "finance_dashboard":"view" (`enabled`) — staf non-finance tidak pernah
  // memicu request ke endpoint Finance sama sekali.
  const { data: finBundle, error: finError } = useDataQuery(
    DASHBOARD_FINANCE_QK,
    () => Promise.all([
      api.get("/finance/dashboard/ceo?months=1"),
      api.get("/finance/piutang/aging-buckets"),
    ]).then(([r1, r2]) => ({ fin: r1.data, aging: r2.data })),
    { enabled: can("finance_dashboard", "view") },
  );
  useEffect(() => {
    if (finError) toast.error("Gagal memuat ringkasan keuangan.");
  }, [finError]);
  const fin = finBundle?.fin ?? null;
  const aging = finBundle?.aging ?? null;

  // Panel "Aktivitas Edit & Hapus" (brief bos #22) — sepuluh log terbaru dari
  // /aktivitas. Gerbangnya sama persis dengan blok Ringkasan Keuangan di atas:
  // hanya ditembak kalau berizin "approval_center":"view".
  const { data: aktivitas, error: aktivitasError } = useDataQuery(
    DASHBOARD_AKTIVITAS_QK,
    () => api.get("/aktivitas?page_size=10").then(r => r.data),
    { enabled: can("approval_center", "view") },
  );
  useEffect(() => {
    if (aktivitasError) toast.error("Gagal memuat aktivitas terbaru.");
  }, [aktivitasError]);

  if (!data) return <div className="p-4 md:p-8"><LoadingState loading data-testid="dashboard-loading" /></div>;

  const showAIInsight = canWidget("ai_insights") && bolehPakaiAI(user, can);
  const showAntrian = canWidget("antrian_cetak") && (can("antrian", "view") || can("antrian", "update")) && !!data.antrian;
  return <DashboardWorkspace key={user.id} data={data} fin={fin} aging={aging} finError={finError}
    aktivitas={aktivitas} aktivitasError={aktivitasError}
    ai={showAIInsight ? <AIInsightCard /> : null}
    weekly={canWidget("wow_sales") && data.wow ? <WowCompareCard wow={data.wow} /> : null}
    attendance={canWidget("wow_attendance") && data.wow?.attendance ? <AttendanceWowCard att={data.wow.attendance} /> : null}
    queue={showAntrian ? <SeksiAntrian antrian={data.antrian} bolehBuka={can("antrian", "view") || can("antrian", "create")} /> : null} />;
}
